export type SapProductCategorization = {
	category: SapProductCategory;
	variant: SapProductVariant;
	dataTransfer: SapProductDataTransfer;
}

export enum SapProductCategory {
	FUNCTIONAL = 'F',
	MICHIGAN_SPLINT = 'FS',
	MEMOTAIN = 'M',
	MEMOTAIN_RETENTIONSSCHIENE_BUNDLE = 'MRB',
	PROFESSIONAL = 'P',
	/** MEMOTAIN CLEAR (former UNITAIN)*/
	RETENTIONSSCHIENE = 'R',
	/** MEMOTAIN PEEK 3D aka. Peek-Retainer (former IVOTAIN)*/
	PEEK_RETAINER = 'PR',
	PEEK_RETAINER_RETENTIONSSCHIENE_BUNDLE = 'PRRB',
	NONE = '-'
}

export enum SapProductVariant {
	SPLINT = 'S',
	DATA = 'D',
	NONE = '-'
}

export enum SapProductDataTransfer {
	DATA_UPLOAD = 'D',
	MODELS = 'M',
	NONE = '-'
}

export enum SapJaws {
	UJ = "OK",
	LJ = "UK",
	BOTH = "OK/UK",
	NONE = "-"
}

export enum SapShippingMethod {
	STANDARD = "Standard",
	EXPRESS = "Express",
	DOWNLOAD = "Download"
}

/**
 * Possible values for `TransportationCode` in a Quotation document.
 * Related to `SapShippingMethod`, but specifying the respective numerical code in SAP
 */
export enum SapTransportationCode {
	TC_STANDARD = 1,
	TC_EXPRESS = 2,
	TC_DOWNLOAD = 3
}

/** Maps the `SapShippingMethod` string enum to the respective numerical `SapTransportationCode` values */
export const SapTransportationCodeForShippingMethod: Record<SapShippingMethod, SapTransportationCode> = {
	[SapShippingMethod.STANDARD]: SapTransportationCode.TC_STANDARD,
	[SapShippingMethod.EXPRESS]: SapTransportationCode.TC_EXPRESS,
	[SapShippingMethod.DOWNLOAD]: SapTransportationCode.TC_DOWNLOAD
}

/** Maps the numerical `SapTransportationCode` enum to the respective `SapShippingMethod` string enum values */
export const SapShippingMethodForTransportationCode: Record<SapTransportationCode, SapShippingMethod> = {
	[SapTransportationCode.TC_STANDARD]: SapShippingMethod.STANDARD,
	[SapTransportationCode.TC_EXPRESS]: SapShippingMethod.EXPRESS,
	[SapTransportationCode.TC_DOWNLOAD]: SapShippingMethod.DOWNLOAD
}

export interface SapShippingCostItem {
	Method: SapShippingMethod;
	Code: SapExpenseCode; 		// expenseCode for SAP shipping item
	Amount: number; 			// price of the composed shipping method
}

export interface SapCostEstimate {
	ItemPriceSum: number,
	DiscountedItemPriceSum: number,
	DiscountFactor: number,
	ShippingCost: number,
	Vat: number,
	Total: number,
	BenefitApplied?: string,
	NumItemsDiscounted?: number
}

export interface SapAddress {
	AddressName: string;
	AddressName2: string;
	AddressName3?: string | null;
	Street: string;
	ZipCode: string;
	City: string;
	Country: string;
	isBillingAddress: boolean;
	companyName: string;
}

export enum SapExpenseCode {
	LIEFERANT_1 = 1,
	UPS_EXPRESS_INLAND_2 = 2,
	UPS_STANDARD_INLAND_3 = 3,
	UPS_STANDARD_AUSLAND_4 = 4,
	UPS_EXPRESS_AUSLAND_5 = 5,

	/** Fake expense code for "free" options, must NOT be sent to SAP as it does not exist. */
	NONE_OR_DOWNLOAD = -1,
}

/** Fake Shipping item for "free" download option, must NOT be sent to SAP as it does not exist. */
export const DOWNLOAD_SHIPPING_ITEM: SapShippingCostItem = {
	Method: SapShippingMethod.DOWNLOAD,
	Code: SapExpenseCode.NONE_OR_DOWNLOAD,
	Amount: 0
}

/** Fake Shipping items for customers that are freed from shipping costs, must NOT be sent to SAP as it does not exist. */
export const FREE_SHIPPING_ITEMS = [
	{
		Method: SapShippingMethod.EXPRESS,
		Code: SapExpenseCode.NONE_OR_DOWNLOAD,
		Amount: 0,
	},
	{
		Method: SapShippingMethod.STANDARD,
		Code: SapExpenseCode.NONE_OR_DOWNLOAD,
		Amount: 0,
	}
];

/** Properties on the BusinessPartner object. */
export enum SapBusinessPartnerProperty {
	/** Name of the customer's company. */
	COMPANY_NAME = "CardName",

	/** Flag indicating whether or not the customer is freed from shipping costs. */
	FREE_SHIPPING_FLAG = "Properties1",
	/** Flag indicating whether or not the customer is getting collective invoices (Sammelrechnung). */
	COLLECTIVE_INVOICE_FLAG = "Properties2",
	/** Flag indicating whether or not the customer is a customer referred to us by GERL. */
	GERL_CUSTOMER_FLAG = "Properties12",
	/** Flag indicating whether or not the customer is a customer referred to us by Cerum (Canada). */
	CERUM_CUSTOMER_FLAG = "Properties20",
	/** Flag indicating whether or not the customer is a customer referred to us by Benco (USA). */
	BENCO_CUSTOMER_FLAG = "Properties21",

	/** List of billing and shipping addresses. */
	ADDRESSES_LIST = "BPAddresses",
	/** 'Production relevant notes' for the customer. */
	PRODUCTION_RELEVANT_NOTES_TEXT = "FreeText",

	/** Divergent billing e-mail address ("REEM": "Rechnungs-Email")*/
	BILLING_EMAIL_ADDRESS = "U_CADREEM",

	/** @deprecated Flag indicating whether or not the customer is in the '2 MEMOTAIN for free' sales promotion. */
	TWO_MEMOTAIN_FREE_FLAG = "Properties9",
	/** @deprecated Flag indicating whether or not the customer is in the '2 IVOTAIN for free' sales promotion. */
	TWO_IVOTAIN_FREE_FLAG = "Properties13",
	/** @deprecated Flag indicating whether or not the customer is in the 'CA Professional 50%' sales promotion. */
	FIFTY_PERCENT_OFF_CA_PRO_FLAG = "Properties14",
}

/** Record of all `SapBusinessPartnerProperty` enum values by their key. */
export const SapBusinessPartnerPropertyRecord: Record<
	keyof typeof SapBusinessPartnerProperty,
	SapBusinessPartnerProperty
> = {
	COMPANY_NAME: SapBusinessPartnerProperty.COMPANY_NAME,
	FREE_SHIPPING_FLAG: SapBusinessPartnerProperty.FREE_SHIPPING_FLAG,
	COLLECTIVE_INVOICE_FLAG: SapBusinessPartnerProperty.COLLECTIVE_INVOICE_FLAG,
	GERL_CUSTOMER_FLAG: SapBusinessPartnerProperty.GERL_CUSTOMER_FLAG,
	CERUM_CUSTOMER_FLAG: SapBusinessPartnerProperty.CERUM_CUSTOMER_FLAG,
	BENCO_CUSTOMER_FLAG: SapBusinessPartnerProperty.BENCO_CUSTOMER_FLAG,
	ADDRESSES_LIST: SapBusinessPartnerProperty.ADDRESSES_LIST,
	PRODUCTION_RELEVANT_NOTES_TEXT: SapBusinessPartnerProperty.PRODUCTION_RELEVANT_NOTES_TEXT,
	BILLING_EMAIL_ADDRESS: SapBusinessPartnerProperty.BILLING_EMAIL_ADDRESS,
	TWO_MEMOTAIN_FREE_FLAG: SapBusinessPartnerProperty.TWO_MEMOTAIN_FREE_FLAG,
	TWO_IVOTAIN_FREE_FLAG: SapBusinessPartnerProperty.TWO_IVOTAIN_FREE_FLAG,
	FIFTY_PERCENT_OFF_CA_PRO_FLAG: SapBusinessPartnerProperty.FIFTY_PERCENT_OFF_CA_PRO_FLAG,
};

/**
 * Possible yes/no values for the BusinessPartner `PropertiesXX` flag attributes in the `SapBusinessPartnerProperty` enum.
 * E.g. `FREE_SHIPPING_FLAG`, `COLLECTIVE_INVOICE_FLAG`, etc.
 */
export enum SapBusinessPartnerPropertyFlagValue {
	YES = "tYES",
	NO = "tNO"
}

export enum SapDocObjectId {
	QUOTATION = "23",
	ORDER = "17",
	DELIVERY_NOTE = "15",
	INVOICE = "13",
	CREDIT_NOTE = "14",
}

export enum SapPdfDocCode {
	QUOTATION = "QUT20010",
	ORDER = "RDR20007",
	INVOICE = "INV20015",
	DELIVERY_NOTE = "DLN20011",
	DECLARATION_OF_CONFORMITY = "DLN20012",
	CREDIT_NOTE = "RIN20009",
}

/** Country-Codes specifying european countries. */
export enum EUCountryCode {
	Austria = 'AT',
	Belgium = 'BE',
	Bulgaria = 'BG',
	Croatia = 'HR',
	Cyprus = 'CY',
	CzechRepublic = 'CZ',
	Denmark = 'DK',
	Estonia = 'EE',
	Finland = 'FI',
	France = 'FR',
	Germany = 'DE',
	Greece = 'GR',
	Greece_EL = 'EL', // XXX Alternative SAP country code for Greece
	Hungary = 'HU',
	Ireland = 'IE',
	Italy = 'IT',
	Latvia = 'LV',
	Lithuania = 'LT',
	Luxembourg = 'LU',
	Malta = 'MT',
	Netherlands = 'NL',
	Poland = 'PL',
	Portugal = 'PT',
	Romania = 'RO',
	Slovakia = 'SK',
	Slovenia = 'SI',
	Spain = 'ES',
	Sweden = 'SE',
}

export const EUCountryCodes = new Set(Object.values(EUCountryCode));

/** Possible values for `BusinessPartner.VatLiable` */
export enum SapVatLiable {
	YES = "Y",
	EUROPE = "E",
	NO = "N"
}

/** Possible values for `BusinessPartner.VatGroup` */
export enum SapVatGroup {
	GERMANY_A1 = "A1",
	EUROPE_A7 = "A7",
	REST_OF_WORLD_A0 = "A0"
}

export enum SapAddressType {
	BILL_TO = 'bo_BillTo',
	SHIP_TO = 'bo_ShipTo'
}

export enum SapDocumentStatus {
	OPEN = "bost_Open",
	CLOSED = "bost_Closed",
}

export enum SapSalesOrderContext {
	PRE_ALIGNER = 'PREAL',
}

export function mapCountryCodeToSapVatLiable(countryCode: string): SapVatLiable {
	if (countryCode === EUCountryCode.Germany) {
		return SapVatLiable.YES;
	} else if (EUCountryCodes.has(countryCode as EUCountryCode)) {
		return SapVatLiable.EUROPE;
	} else {
		return SapVatLiable.NO;
	}
}

export function mapCountryCodeToSapVatGroup(countryCode: string): SapVatGroup {
	if (countryCode === EUCountryCode.Germany) {
		return SapVatGroup.GERMANY_A1;
	} else if (EUCountryCodes.has(countryCode as EUCountryCode)) {
		return SapVatGroup.EUROPE_A7;
	} else {
		return SapVatGroup.REST_OF_WORLD_A0;
	}
}

/** SAP language codes. */
export enum SapLanguageCode {
	ENGLISH = 3,
	GERMAN = 9,
}

enum SapPdfDocNumPrefix {
	COST_ESTIMATE = "1",
	ORDER = "2",
	/**
	 * Prefix '3' is shared and can only be separated by DocCode (see `SapPdfDocCode`)
	 * Delivery Note: 				'DocCode = DLN20011'
	 * Declaration of conformity: 	'DocCode = DLN20012'
	 */
	DELIVERY_NOTE_CONFORMITY = "3",
	INVOICE = "4",
	CREDIT_NOTE = "5",
}

/** PDF filename prefixes for English language. */
enum SapPdfFilenamePrefixEN {
	COST_ESTIMATE = "cost_estimate",
	ORDER = "order",
	DELIVERY_NOTE = "delivery_note",
	DECLARATION_OF_CONFORMITY = "declaration_of_conformity",
	INVOICE = "invoice",
	CREDIT_NOTE = "credit_note",
}

/** PDF filename prefixes for German language, mapped from English enum values. */
const SapPdfFilenamePrefixDEFromEN: Record<SapPdfFilenamePrefixEN, string> = {
	[SapPdfFilenamePrefixEN.COST_ESTIMATE]: "Kostenvoranschlag",
	[SapPdfFilenamePrefixEN.ORDER]: "Auftrag",
	[SapPdfFilenamePrefixEN.DELIVERY_NOTE]: "Lieferschein",
	[SapPdfFilenamePrefixEN.DECLARATION_OF_CONFORMITY]: "Konformitätserklärung",
	[SapPdfFilenamePrefixEN.INVOICE]: "Rechnung",
	[SapPdfFilenamePrefixEN.CREDIT_NOTE]: "Gutschrift",
};

/**
 * Maps the `SapPdfDocNumPrefix` string enum to the respective `SapPdfFilenamePrefix` values
 * Function is not on 'export' (aka. private) since the special handling is needed
*/
const SapPdfFilenameFromDocNumPrefix: Record<SapPdfDocNumPrefix, SapPdfFilenamePrefixEN> = {
	[SapPdfDocNumPrefix.COST_ESTIMATE]: SapPdfFilenamePrefixEN.COST_ESTIMATE,
	[SapPdfDocNumPrefix.ORDER]: SapPdfFilenamePrefixEN.ORDER,
	[SapPdfDocNumPrefix.DELIVERY_NOTE_CONFORMITY]: SapPdfFilenamePrefixEN.DELIVERY_NOTE, //needs a special handling
	[SapPdfDocNumPrefix.INVOICE]: SapPdfFilenamePrefixEN.INVOICE,
	[SapPdfDocNumPrefix.CREDIT_NOTE]: SapPdfFilenamePrefixEN.CREDIT_NOTE,
}

/**
 * Returns the composed pdf file name which is determined from the 'docNum'
 * Since 'Lieferschein' and 'Konf-Erklärung' share the same prefix, the
 * record `SapPdfFilenameFromDocNumPrefix` is just not sufficient and we need
 * a special handling.
 * @param docNum 'docNum' for the related file in SAP
 * @param docCode Only needed for 'Lieferschein' and 'Konformitätserklärung'
 * @param languageCode The SAP language code for the filename prefix (default: English)
 * @returns the composed filename existing of 'filetype' and 'docNum'
 */
export function mapDocNumPrefixToPdfFilename(
	docNum: string,
	docCode?: SapPdfDocCode,
	languageCode: SapLanguageCode = SapLanguageCode.ENGLISH
): string {
	const docNumPrefix = docNum.charAt(0) as SapPdfDocNumPrefix;
	let filenamePrefix: string = SapPdfFilenameFromDocNumPrefix[docNumPrefix] ?? "document";

	// Special handling for 'Konformitätserklärung'
	if (
		docNumPrefix === SapPdfDocNumPrefix.DELIVERY_NOTE_CONFORMITY &&
		docCode === SapPdfDocCode.DECLARATION_OF_CONFORMITY
	) {
		filenamePrefix = SapPdfFilenamePrefixEN.DECLARATION_OF_CONFORMITY;
	}

	// Special handling for German language
	if (languageCode === SapLanguageCode.GERMAN) {
		filenamePrefix = SapPdfFilenamePrefixDEFromEN[filenamePrefix] ?? "Dokument";
	}

	return `${filenamePrefix}_${docNum}.pdf`;
}

/**
 * Custom HTTP header that must be set to `true` to handle array fields as replacements
 * for existing collection fields, such as `DocumentLines` in a quotation PATCH payload.
 * See: https://community.sap.com/t5/enterprise-resource-planning-blogs-by-sap/sap-business-one-service-layer-entity-crud-update/ba-p/13171993
 */
export const SAP_SL_REPLACE_COLLECTIONS_HEADER = "B1S-ReplaceCollectionsOnPatch";
